import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Modal from "components/basic/modal";
import ScrollContainer from "components/basic/scrollContainer";
import Typography from "components/basic/typography";
import { QuoteApprovalStatus, useReviseQuoteMutation } from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import PricingSection from "./components/PricingSection";
import AddOnsSection from "./components/AddOnsSection";
import TotalsPreview from "./components/TotalsPreview";
import ContentSection from "./components/ContentSection";
import ValiditySection from "./components/ValiditySection";
import VendorNoteSection from "./components/VendorNoteSection";
import ModalFooter from "./components/ModalFooter";
import { draftErrors, toDraft, toInput } from "./helpers";
import { LeadQuote, ReviseQuoteDraft } from "./types";

export interface ReviseQuoteModalProps {
  quote: LeadQuote;
  /** Called after a successful save, so the leads list can refetch. */
  onSaved: () => void;
  onClose: () => void;
}

/**
 * Lets a vendor correct a quote they already issued. Saving re-prices it
 * server-side and sends it back to the customer for approval.
 */
const ReviseQuoteModal: React.FC<ReviseQuoteModalProps> = ({
  quote,
  onSaved,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("leads");
  const showSnack = useSnack();
  const [{ fetching: saving }, reviseQuote] = useReviseQuoteMutation();
  const [draft, setDraft] = useState<ReviseQuoteDraft>(() => toDraft(quote));

  const patch = (changes: Partial<ReviseQuoteDraft>) =>
    setDraft((prev) => ({ ...prev, ...changes }));

  const errors = useMemo(() => draftErrors(draft, t), [draft, t]);

  const handleSave = async () => {
    const result = await reviseQuote({
      id: quote.id,
      input: toInput(draft),
    });
    const payload = result.data?.reviseQuote;

    if (payload?.quote) {
      showSnack(t("revise.saved"), "success");
      onSaved();
      onClose();
      return;
    }

    showSnack(
      payload?.errors?.[0]?.message ?? t("revise.save_failed"),
      "error",
    );
  };

  return (
    <Modal onClose={onClose}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2),
            width: "min(640px, 86vw)",
          }}
        >
          <div>
            <Typography variant="h5">{t("revise.title")}</Typography>
            <Typography
              variant="body2"
              style={{ color: theme.palette.text.secondary }}
            >
              {t("revise.subtitle", {
                estimate: quote.estimateNumber,
                customer: `${quote.chargerServiceRequest.account.firstName} ${quote.chargerServiceRequest.account.lastName}`,
              })}
            </Typography>
          </div>

          <ScrollContainer height="min(60vh, 520px)">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(3),
                padding: theme.spacing(0.5),
              }}
            >
              <PricingSection draft={draft} onChange={patch} />
              <AddOnsSection
                addOns={draft.addOns}
                onChange={(addOns) => patch({ addOns })}
              />
              <TotalsPreview
                draft={draft}
                taxRate={quote.taxRate}
                taxLabel={quote.taxLabel}
              />
              <ContentSection draft={draft} onChange={patch} />
              <ValiditySection
                draft={draft}
                quoteValidFor={quote.evChargerService.quoteValidFor}
                onChange={patch}
              />
              <VendorNoteSection draft={draft} onChange={patch} />
            </div>
          </ScrollContainer>

          <ModalFooter
            errors={errors}
            warnReapproval={
              quote.approvalStatus === QuoteApprovalStatus.Approved
            }
            saving={saving}
            onSave={handleSave}
            onClose={onClose}
          />
        </div>
      </LocalizationProvider>
    </Modal>
  );
};

export default ReviseQuoteModal;
